<template>
  <f7-page>
    <f7-navbar
      :title="$t('tontine.cycleTitle', { number: cycle?.cycleNumber || 1 })"
      back-link
    />
    <AppLayout>
      <template #main>
        <f7-block v-if="loading" strong inset>
          <f7-preloader /> {{ $t("tontine.loading") }}
        </f7-block>

        <template v-else-if="cycle">
          <!-- Cycle summary -->
          <f7-block strong inset>
            <div class="cycle-head">
              <div>
                <h2>
                  {{
                    $t("tontine.currentCycle", { number: cycle.cycleNumber })
                  }}
                </h2>
                <f7-badge :class="payoutStatusClass">{{
                  payoutStatusLabel
                }}</f7-badge>
              </div>
              <f7-progressbar
                v-if="progressPercent"
                :progress="progressPercent"
                color="green"
                style="width: 100%; margin-top: 10px"
              />
            </div>
            <f7-list no-hairlines>
              <f7-list-item
                :title="$t('tontine.dueDate')"
                :after="formatDate(cycle.dueDate)"
              />
              <f7-list-item
                :title="$t('tontine.payoutDate')"
                :after="formatDate(cycle.payoutDate)"
              />
              <f7-list-item
                :title="$t('tontine.beneficiary')"
                :after="
                  cycle.beneficiaryDisplayNameSnapshot || $t('tontine.toDefine')
                "
              />
              <f7-list-item
                :title="$t('tontine.expectedTotal')"
                :after="money(cycle.expectedTotal)"
              />
              <f7-list-item
                :title="$t('tontine.confirmedTotal')"
                :after="money(cycle.confirmedTotal)"
              />
            </f7-list>
          </f7-block>

          <!-- Payout block -->
          <f7-block v-if="payout" strong inset>
            <div class="payout-ready">
              <strong>{{ $t("tontine.payoutPrepared") }}</strong>
              <p>
                {{ money(payout.amount) }} →
                {{
                  payout.beneficiaryDisplayName ||
                  cycle.beneficiaryDisplayNameSnapshot
                }}
                · {{ payoutStatusDetailed(payout.status) }}
              </p>
              <f7-button
                small
                outline raised round
                @click="openPayout('payout', payout.id)"
                >{{ $t("tontine.viewPayout") }}</f7-button
              >
            </div>
          </f7-block>
          <f7-block v-else-if="plan?.canManage" strong inset>
            <div class="payout-guide">
              <strong>{{ $t("tontine.payoutReadyTitle") }}</strong>
              <p>{{ $t("tontine.payoutReadyBody") }}</p>
              <f7-button
                fill
                small round raised
                :disabled="confirmedCount < 1"
                @click="preparePayoutDialog"
              >
                {{ $t("tontine.preparePayout") }}
              </f7-button>
              <p v-if="confirmedCount < 1" class="hint">
                {{ $t("tontine.payoutNeedsConfirmation") }}
              </p>
            </div>
          </f7-block>

          <!-- Contributions -->
          <f7-block-title
            >{{ $t("tontine.memberContributions") }} ({{
              contributions.length
            }})</f7-block-title
          >
          <f7-list>
            <f7-list-item
              v-for="contribution in contributions"
              :key="contribution.id"
              :title="contribution.memberDisplayName || $t('tontine.member')"
              @click="
                canOpen(contribution) && openContribution(contribution.id)
              "
            >
              <template #after>
                <f7-badge
                  :class="contributionStatusClass(contribution.status)"
                  >{{ contributionStatusLabel(contribution.status) }}</f7-badge
                >
              </template>
            </f7-list-item>
          </f7-list>

          <!-- Contribution actions -->
          <f7-block v-if="myContribution && canActNow" strong inset>
            <f7-button fill large raised round class="mb-8" @click="payNow">{{
              $t("tontine.payOrProvideProof")
            }}</f7-button>
          </f7-block>

          <!-- Regulatory / pedagogical notice -->
          <f7-block strong inset class="info-notice">
            <p class="text-color-gray">
              <strong>{{ $t("tontine.infoNoticeTitle") }}</strong
              ><br />
              {{ $t("tontine.infoNoticeBody") }}
            </p>
          </f7-block>
        </template>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import { useI18n } from "vue-i18n";
import { useTontineStore } from "@/js/pinia-stores/tontine";
import { useKeycloakStore } from "@/js/pinia-stores/keycloak";
import usePaymentTrigger from "@/js/composables/usePaymentTrigger";
import AppLayout from "../../components/common/AppLayout.vue";

const { t } = useI18n();
const props = defineProps({
  campaignId: String,
  groupId: String,
  cycleId: String,
});
const store = useTontineStore();
const keycloak = useKeycloakStore();
const payment = usePaymentTrigger();

const plan = computed(() => store.plan);
const cycleDetail = computed(() => store.cycleDetail);
const loading = computed(() => store.loading);
const subject = computed(() => keycloak.subject);

const cycle = computed(() => cycleDetail.value?.cycle);
const contributions = computed(() => cycleDetail.value?.contributions || []);
const payout = computed(() => cycleDetail.value?.payout);
const confirmedCount = computed(
  () => contributions.value.filter((c) => c.status === "CONFIRMED").length,
);
const progressPercent = computed(() => {
  if (!cycle.value?.expectedTotal) return 0;
  return Math.min(
    100,
    Math.round(
      ((cycle.value.confirmedTotal || 0) / cycle.value.expectedTotal) * 100,
    ),
  );
});
const myContribution = computed(() =>
  contributions.value.find((c) => c.memberUserId === subject.value),
);
const canActNow = computed(
  () =>
    myContribution.value &&
    ["DUE", "PAYMENT_REQUESTED", "OVERDUE"].includes(
      myContribution.value.status,
    ),
);

onMounted(load);

async function load() {
  try {
    await Promise.all([
      store.loadSummary(props.campaignId),
      store.fetchCycleDetail(props.campaignId, props.cycleId),
    ]);
  } catch (err) {
    f7.dialog.alert(
      err.response?.data?.message || err.message || t("tontine.loadError"),
    );
  }
}

function payNow() {
  if (!myContribution.value) return;
  if (plan.value?.operatingMode === "COORDINATION_ONLY") {
    openContribution(myContribution.value.id);
    return;
  }
  payment.triggerCampaignContribution({
    campaignId: props.campaignId,
    groupId: props.groupId,
    amount: myContribution.value.expectedAmount,
    campaignType: "TONTINE",
    currency: plan.value?.currency,
    campaignName: plan.value?.campaignName,
    description: t("tontine.contributionForCycle", {
      number: cycle.value?.cycleNumber || 1,
    }),
  });
}

function openContribution(contributionId) {
  f7.views.main.router.navigate(
    `/groups/${props.groupId}/campaigns/${
      props.campaignId
    }/tontine/contributions/${contributionId}${
      cycle.value?.id ? `?cycleId=${cycle.value.id}` : ""
    }`,{props: { campaignId: props.campaignId, groupId: props.groupId, cycleId: cycle.value?.id }}
  );
}

function openPayout(type, id) {
  f7.views.main.router.navigate(
    `/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/payouts/${id}`,{props: { campaignId: props.campaignId, groupId: props.groupId, cycleId: cycle.value?.id }}
  );
}

function preparePayoutDialog() {
  const defAmount =
    cycle.value?.confirmedTotal || cycle.value?.expectedTotal || 0;
  f7.dialog.prompt(
    t("tontine.preparePayoutPrompt", {
      amount: money(defAmount),
      beneficiary: cycle.value?.beneficiaryDisplayNameSnapshot,
    }),
    t("tontine.preparePayoutTitle"),
    (amountRaw) => {
      const amount = Number(String(amountRaw || "").replace(/[^0-9]/g, ""));
      if (!amount || amount <= 0) {
        f7.dialog.alert(t("common.enterValidAmount"));
        return;
      }
      preparePayout(amount);
    },
    String(defAmount),
  );
}

async function preparePayout(amount) {
  f7.preloader.show();
  try {
    const result = await store.preparePayout(props.campaignId, props.cycleId, {
      beneficiaryUserId: cycle.value?.beneficiaryUserId,
      amount,
      currency: plan.value?.currency || "XOF",
    });
    f7.toast.show({ text: t("tontine.payoutPrepared"), position: "bottom" });
    if (result?.id) openPayout("payout", result.id);
    else await load();
  } catch (err) {
    f7.dialog.alert(
      err.response?.data?.message ||
        err.message ||
        t("tontine.preparePayoutError"),
    );
  } finally {
    f7.preloader.hide();
  }
}

function canOpen(contribution) {
  return plan.value?.canManage || contribution.memberUserId === subject.value;
}

const payoutStatusDetailed = (status) =>
  payoutStatusLabelMap()[status] || status;
const payoutStatusLabelMap = () => ({
  NOT_READY: t("tontine.payoutStatus.NOT_READY"),
  DRAFT: t("tontine.payoutStatus.DRAFT"),
  AWAITING_APPROVAL: t("tontine.payoutStatus.AWAITING_APPROVAL"),
  APPROVED: t("tontine.payoutStatus.APPROVED"),
  SUBMITTED_TO_PARTNER: t("tontine.payoutStatus.SUBMITTED_TO_PARTNER"),
  EXECUTED: t("tontine.payoutStatus.EXECUTED"),
  FAILED: t("tontine.payoutStatus.FAILED"),
  CANCELLED: t("tontine.payoutStatus.CANCELLED"),
});
const payoutStatusLabel = computed(
  () =>
    payoutStatusLabelMap()[cycle.value?.payoutStatus] ||
    cycle.value?.payoutStatus,
);
const payoutStatusClass = computed(() => {
  const s = cycle.value?.payoutStatus;
  return s === "EXECUTED"
    ? "badge-green"
    : s === "FAILED" || s === "CANCELLED"
    ? "badge-red"
    : s
    ? "badge-orange"
    : "badge-gray";
});

const contributionStatusLabel = (s) =>
  ({
    DUE: t("tontine.contributionStatus.DUE"),
    PAYMENT_REQUESTED: t("tontine.contributionStatus.PAYMENT_REQUESTED"),
    PAYMENT_PENDING_CONFIRMATION: t(
      "tontine.contributionStatus.PAYMENT_PENDING_CONFIRMATION",
    ),
    RECEIPT_SUBMITTED: t("tontine.contributionStatus.RECEIPT_SUBMITTED"),
    CONFIRMED: t("tontine.contributionStatus.CONFIRMED"),
    OVERDUE: t("tontine.contributionStatus.OVERDUE"),
    NOT_DUE: t("tontine.contributionStatus.NOT_DUE"),
    EXCUSED: t("tontine.contributionStatus.EXCUSED"),
    WAIVED: t("tontine.contributionStatus.WAIVED"),
    REVERSED: t("tontine.contributionStatus.REVERSED"),
  }[s] ||
  s ||
  "—");

const contributionStatusClass = (s) => {
  if (s === "CONFIRMED") return "badge-green";
  if (s === "OVERDUE" || s === "REVERSED") return "badge-red";
  if (s === "EXCUSED" || s === "WAIVED") return "badge-gray";
  if (s === "RECEIPT_SUBMITTED" || s === "PAYMENT_PENDING_CONFIRMATION")
    return "badge-orange";
  return "badge-yellow";
};

function money(v, c) {
  return new Intl.NumberFormat(keycloak.locale || "fr", {
    style: "currency",
    currency: c || plan.value?.currency || "XOF",
    maximumFractionDigits: 0,
  }).format(v || 0);
}
function formatDate(v) {
  return v
    ? new Intl.DateTimeFormat(keycloak.locale || "fr", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(
        new Date(v.startsWith("20") && v.length === 10 ? v + "T00:00:00" : v),
      )
    : "—";
}
</script>

<style scoped>
.cycle-head h2 {
  margin: 0 0 8px;
  font-size: 22px;
}
.cycle-head .badge-green {
  background: #00b894;
}
.cycle-head .badge-orange {
  background: #f0932b;
}
.cycle-head .badge-red {
  background: #ee5253;
}
.cycle-head .badge-gray {
  background: #9e9e9e;
}
.badge-green {
  background: #00b894;
}
.badge-orange {
  background: #f0932b;
}
.badge-red {
  background: #ee5253;
}
.badge-gray {
  background: #9e9e9e;
}
.badge-yellow {
  background: #fdcb6e;
  color: #333;
}
.payout-ready,
.payout-guide {
  padding: 4px 0;
}
.payout-ready p,
.payout-guide p {
  font-size: 13px;
  color: var(--f7-on-surface-variant);
  margin: 6px 0;
}
.hint {
  font-size: 12px;
  color: var(--f7-on-surface-variant);
}
.mb-8 {
  margin-bottom: 12px;
}
.info-notice p {
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}
</style>
